/**
 * The line under Shortage Qty (CR-7485 rework, Aaron 28 Oct 2026).
 *
 * With a goods receipt linked it shows what the receipt's PO line still owes:
 * ordered, less what its receipts delivered, less shortage other NCRs already
 * record. The server refuses more than that on save. With no GRN linked it says
 * nothing; the server still caps against the NCR's PO line.
 */
import { useEffect, useState } from 'react';
import { qualityAPI } from '../../lib/api';
import type { ShortageAvailability } from '../../lib/api/quality';

export interface ShortageQtyHintProps {
  /** The linked goods receipt, or null when none is linked. */
  grnId: number | null | undefined;
  /** The NCR being edited, so its own shortage is not counted as "elsewhere". */
  ncrId?: number;
  shortageQty: number | string | null | undefined;
}

const toNumber = (v: number | string | null | undefined): number => {
  const n = typeof v === 'number' ? v : parseInt(String(v ?? ''), 10);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Still-owed hint for the NCR's Shortage Qty input.
 *
 * @param props - {@link ShortageQtyHintProps}
 * @returns the hint line, or nothing when there is nothing to measure against
 */
export default function ShortageQtyHint({ grnId, ncrId, shortageQty }: ShortageQtyHintProps) {
  const [avail, setAvail] = useState<ShortageAvailability | null>(null);

  useEffect(() => {
    let live = true;
    setAvail(null);
    if (!grnId) return;
    qualityAPI.getRejectAvailability(grnId, ncrId)
      .then(res => { if (live) setAvail(res.data.shortage ?? null); })
      .catch((err: unknown) => { console.error('Shortage availability lookup failed:', err); });
    return () => { live = false; };
  }, [grnId, ncrId]);

  if (!grnId || !avail) return null;
  const over = toNumber(shortageQty) > avail.available;
  return (
    <p className={`text-xs mt-1 tabular-nums ${over ? 'text-red-600' : 'text-muted-foreground'}`} role={over ? 'alert' : undefined}>
      Still owed on this PO line: {avail.available} (ordered {avail.ordered}, delivered {avail.delivered}
      {avail.short_elsewhere ? `, ${avail.short_elsewhere} already short on other NCRs` : ''})
      {over ? ` · only ${avail.available} can be recorded short` : ''}
    </p>
  );
}
